/**
 * initKeyboardViewport — keep the chat composer above the on-screen keyboard.
 *
 * iOS WKWebView does NOT shrink the layout viewport for the keyboard (100dvh
 * stays full, only visualViewport.height moves), so a bottom composer hides
 * behind it — we shrink the shell ourselves (outerLayout reads
 * height: var(--app-vh, 100dvh)). This is the CONFIRMED-WORKING iOS approach.
 *
 * Android Chrome WebView with @capacitor/keyboard resize:"native" ALREADY
 * resizes the WebView, so 100dvh tracks the keyboard and our shrink would
 * double-adjust (header-hide jump, scroll yo-yo, flicker). So we hard-skip the
 * shim on Android (Capacitor.getPlatform()==='android') and let resize:native
 * own it. isNativeApp()/the UA marker can't tell the platforms apart (same
 * WhapizCRM-Native UA on both), so we use getPlatform().
 */
let attached = false;

export function initKeyboardViewport() {
  if (attached || typeof window === "undefined") return;
  const vv = window.visualViewport;
  if (!vv) return;
  attached = true;

  const root = document.documentElement;
  let pending = false;
  // Largest visible height seen = keyboard-closed full height. Re-baselined on
  // rotation (otherwise a portrait max permanently shrinks landscape).
  let maxH = vv.height;

  const apply = () => {
    pending = false;
    // Android already resizes the WebView for the keyboard → never shim there.
    if (window.Capacitor && typeof window.Capacitor.getPlatform === "function"
        && window.Capacitor.getPlatform() === "android") {
      root.classList.remove("kb-open");
      root.style.removeProperty("--app-vh");
      return;
    }
    if (vv.height > maxH) maxH = vv.height;
    const kbOpen = maxH - vv.height > 80;
    root.classList.toggle("kb-open", kbOpen);
    if (kbOpen) {
      root.style.setProperty("--app-vh", `${Math.round(vv.height)}px`);
      if (window.pageYOffset !== 0) window.scrollTo(0, 0);
    } else {
      root.style.removeProperty("--app-vh");
    }
  };

  const onChange = () => {
    if (!pending) {
      pending = true;
      requestAnimationFrame(apply);
    }
  };
  // Rotation: re-baseline maxH to the new orientation's full height so the
  // shell isn't left permanently shrunk after portrait↔landscape.
  const onRotate = () => {
    maxH = vv.height;
    onChange();
  };

  // React to the keyboard opening/closing (a RESIZE of the visual viewport)
  // and to rotation — NOT to scroll. The visualViewport "scroll" event fires
  // continuously during normal scrolling/rubber-band; running apply() there let
  // a transient height dip flip kbOpen, resize the shell via --app-vh and force
  // scrollTo(0,0) mid-scroll — the chat "jitter". The keyboard is never a scroll
  // event, so dropping this listener removes the churn with no loss.
  vv.addEventListener("resize", onChange);
  window.addEventListener("orientationchange", onRotate);
  onChange();
}

export default initKeyboardViewport;
